import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import throttle from 'lodash/throttle';
import isEmpty from 'lodash/isEmpty';
import { fetchPlacements } from 'core/actions/ocapi';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import MerchantExperienceLink from 'core/modules/MerchantExperienceLink/MerchantExperienceLink';
import MerchantWithOfferCountTile from 'core/modules/MerchantWithOfferCountTile/MerchantWithOfferCountTile';
import List from 'core/modules/Partials/List/List';
import MerchantTileListItem from 'core/modules/MerchantTileList/components/MerchantTileListItem/MerchantTileListItem';

import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import config from 'core/modules/MarketingMerchantsHome/configs';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';

import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import { selectUserIsIdentified } from 'core/selectors/user';
import { selectMhpMarketingSectionEnabled } from 'core/selectors/app';

import { triggerMarketingMerchantsHomeIsViewed } from './actions';

import {
  selectMerchantConfig, selectMerchantIsLoaded, selectMerchantIsLoading, selectMerchantData,
  selectTitlesIsLoading, selectTitlesIsLoaded, selectTitleData, selectTitlesConfig,
} from './selectors';

import './MarketingMerchantsHome.scss';

function MarketingMerchantsHome() {
  const merchantsIsLoaded = useSelector(selectMerchantIsLoaded);
  const merchantsIsLoading = useSelector(selectMerchantIsLoading);
  const merchantsConfig = useSelector(selectMerchantConfig);
  const merchantData = useSelector(selectMerchantData);
  const titleConfig = useSelector(selectTitlesConfig);
  const titleIsLoaded = useSelector(selectTitlesIsLoaded);
  const titleIsLoading = useSelector(selectTitlesIsLoading);
  const titleData = useSelector(selectTitleData);
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const userIsIdentified = useSelector(selectUserIsIdentified);
  const isMhpMarketingSectionEnabled = useSelector(selectMhpMarketingSectionEnabled);
  const merchantsHomeElement = useRef();

  const dispatch = useDispatch();

  const { api: { params: merchantsParams, options: merchantsOptions } } = merchantsConfig;
  const { api: { params: titleParams, options: titleOptions } } = titleConfig;

  useEffect(() => {
    if (!merchantsIsLoaded && !merchantsIsLoading) {
      dispatch(fetchPlacements(merchantsParams, merchantsOptions));
    }
  }, []);

  useEffect(() => {
    if (!titleIsLoaded && !titleIsLoading) {
      dispatch(fetchPlacements(titleParams, titleOptions));
    }
  }, []);

  // eslint-disable-next-line no-use-before-define
  const throttledHandleMarketingMerchantsHomeGaEvents = throttle(handleMarketingMerchantsHomeGaEvents, 500);

  function handleMarketingMerchantsHomeGaEvents() {
    const isSectionVisibleInViewport = merchantsHomeElement.current?.getBoundingClientRect().top <= window.innerHeight;
    const isSectionLoaded = merchantsIsLoaded && titleIsLoaded;

    if (isSectionVisibleInViewport && isSectionLoaded) {
      dispatch(triggerMarketingMerchantsHomeIsViewed());
      window.removeEventListener('scroll', throttledHandleMarketingMerchantsHomeGaEvents);
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleMarketingMerchantsHomeGaEvents);

    return () => {
      window.removeEventListener('scroll', throttledHandleMarketingMerchantsHomeGaEvents);
    };
  }, [merchantsIsLoaded, titleIsLoaded]);

  const {
    carousel,
    useAddToFavorite,
    shouldHideIncompleteRow,
    shouldUseCarousel,
    secTitlePill,
    shouldRenderLinkInsideTileListOnMobile,
    rebateOptions,
  } = config;

  const shouldRenderLinkInsideTileList = isMobileMQ && shouldRenderLinkInsideTileListOnMobile;

  if (isEmpty(merchantData) || isEmpty(titleData) || !isMhpMarketingSectionEnabled) {
    return null;
  }

  const { assets, clickUrl } = titleData;

  const renderViewAllTileLink = () => (
    <a className="mn_marketingMerchantsHomeViewAllLink" href={clickUrl}>
      <div>{assets.callToActionText}</div>
    </a>
  );

  const renderTilesWithOffers = () => (
    <List options={carousel} shouldUseCarousel>
      { merchantData.map(merchant => (
        <MerchantExperienceLink merchant={merchant} key={merchant.id}>
          <MerchantWithOfferCountTile merchant={merchant} rebateOptions={rebateOptions} />
        </MerchantExperienceLink>
      ))}
      {shouldRenderLinkInsideTileList && renderViewAllTileLink()}
    </List>
  );

  const renderTilesWithoutOffers = () => (
    <List
      options={carousel}
      shouldUseCarousel={shouldUseCarousel}
      shouldHideIncompleteRow={shouldHideIncompleteRow}
    >
      {merchantData.map((merchant) => (
        <MerchantTileListItem
          key={merchant.id}
          merchant={merchant}
          useAddToFavorite={useAddToFavorite}
          useSmallerIcon={!isMobileMQ}
          label={secTitlePill}
        />
      ))}
      {shouldRenderLinkInsideTileList && renderViewAllTileLink()}
    </List>
  );

  return (
    <div className="mn_marketingMerchantsHome" ref={merchantsHomeElement}>
      <ContentWrap mobile tablet l xl>
        <SectionTitle title={assets.shortSummaryText}>
          {!shouldRenderLinkInsideTileList &&
          <a className="mn_viewAllLink" href={clickUrl}>{assets.callToActionText}</a>
          }
        </SectionTitle>
        {(isMobileMQ && userIsIdentified) ? renderTilesWithOffers() : renderTilesWithoutOffers()}
      </ContentWrap>
    </div>
  );
}
export default (props) => <ComponentErrorBoundary><MarketingMerchantsHome {...props} /></ComponentErrorBoundary>;
